<script>
import MultiSelect from 'primevue/multiselect';
import examEnhancements from "@/mixins/examEnhancements";
import Breadcrumb from '@/components/global/breadcrumb.vue';
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import { useGradeStore } from '@/stores/useGradeStore.js';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import DatePicker from 'primevue/datepicker';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

export default {
  mixins: [examEnhancements],
  name: "edit-exam",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      searchQuery: '',
      examId: this.$route.params.id,
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      title: "",
      date: null,
      selectedQuestions: [],
      questions: [],
      from_time: null,
      to_time: null,
      lesson: null,
      lessons: [],
      chapter: null,
      chapters: [],
      duration_required: false,
      question_order: 'fixed', // 'fixed' = same order for all; 'random' = per-student
      questionOrderOptions: [
        { label: 'نفس الترتيب لكل الطلاب', value: 'fixed', icon: 'pi-sort-alt' },
        { label: 'ترتيب عشوائي لكل طالب', value: 'random', icon: 'pi-sort-alt-slash' },
      ],
      navigation_mode: 'free', // 'free' | 'sequential' | 'sequential_back'; default free
      navigationModeOptions: [
        { label: 'سؤال بسؤال بدون رجوع', value: 'sequential', icon: 'pi-arrow-left',
          desc: 'يجب الإجابة على كل سؤال بالترتيب ولا يمكن الرجوع للأسئلة السابقة' },
        { label: 'سؤال بسؤال مع إمكانية الرجوع', value: 'sequential_back', icon: 'pi-replay',
          desc: 'يجب الإجابة بالترتيب، ويمكن الرجوع للأسئلة التي تم الوصول إليها' },
        { label: 'تنقل حر', value: 'free', icon: 'pi-arrows-h',
          desc: 'يمكن للطالب التنقل بين جميع الأسئلة بحرية' },
      ],
      formSending: false,
      loading: true,
    };
  },
  validations() {
    return {
      title: { required },
      date: { required },
      question_ids: this.examSettings.automated ? {} : { required },
      lesson: this.examSettings.automated ? {} : { required },
    };
  },
  components: {
    MultiSelect,
    Breadcrumb,
    Dropdown,
    Calendar,
    InputText,
    InputSwitch,
    DatePicker,
    DataTable,
    Column
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/exams` : `/${this.gradeSlug}/exams`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "الامتحانات", path: this.basePath }, { name: "تعديل امتحان", path: `${this.basePath}/edit/${this.examId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "الامتحانات", path: this.basePath },
        { name: "تعديل امتحان", path: `/${this.gradeSlug}/exam/edit/${this.examId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
    customLabelChapter() {
      return option => option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
    customLabelLesson() {
      return option => option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
    duration() {
      if (!this.from_time || !this.to_time) return null;

      const getDateObject = (value) => (value instanceof Date ? value : new Date(value));

      let start = getDateObject(this.from_time);
      let end = getDateObject(this.to_time);

      const today = new Date();
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), start.getHours(), start.getMinutes());
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), end.getHours(), end.getMinutes());

      let diffMs = end - start;
      if (diffMs < 0) {
        end.setDate(end.getDate() + 1);
        diffMs = end - start;
      }

      const diffMinutes = Math.floor(diffMs / 60000);
      if (diffMinutes <= 0 || diffMinutes > 1440) return null;

      return diffMinutes;
    },
    question_ids: {
      get() {
        return this.selectedQuestions.map(q => q.id);
      },
      set(ids) {
        this.selectedQuestions = this.questions.filter(q => ids.includes(q.id));
      }
    },
    totalDegree() {
      return this.selectedQuestions.reduce((sum, q) => sum + (q.degree || 0), 0);
    },
    filteredQuestions() {
      const q = (this.searchQuery || '').toString().trim().toLowerCase();
      if (!q) return this.questions;
      return this.questions.filter(item => {
        const title = (item.title || '').toString().toLowerCase();
        const type = (item.type || '').toString().toLowerCase();
        const degree = (item.degree || '').toString().toLowerCase();
        const difficulty = (this.difficultyLabel(item.difficulty_level) || '').toString().toLowerCase();
        return title.includes(q) || type.includes(q) || degree.includes(q) || difficulty.includes(q);
      });
    }
  },
  methods: {
    fetchChapters() {
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId }
          : { type: 2, academic_year_id: this.gradeId };
      return axios.get('/lessons', { params })
          .then(res => this.chapters = res.data.data)
          .catch(() => this.chapters = []);
    },
    fetchLessons() {
      if (!this.chapter?.id) return Promise.resolve();
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: this.chapter.id }
          : { type: 3, academic_year_id: this.gradeId, parent_id: this.chapter.id };
      return axios.get('/lessons', { params })
          .then(res => { this.lessons = res.data.data; })
          .catch(() => this.lessons = []);
    },
    fetchQuestions() {
      const params = this.isCourseScope ? { course_id: this.courseId } : { academic_year_id: this.gradeId };
      const scope = this.lesson || this.unit || this.chapter || this.branch;
      if (scope?.id) params.lesson_id = scope.id;
      if (this.lesson_ids?.length) {
        delete params.lesson_id;
        params.lesson_ids = this.lesson_ids.map(l => l.id || l);
      }
      return axios.get('/questions', { params })
          .then(res => this.questions = res.data.data)
          .catch(() => this.questions = []);
    },
    parseTimeString(str) {
      if (!str) return null;
      const [h, m] = str.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d;
    },
    formatDate(date) {
      if (!date) return "";
      let d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d)) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatTimeForBackend(date, use24Hour = false) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d)) return '';
      const hours24 = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      if (use24Hour) {
        return `${hours24.toString().padStart(2, '0')}:${minutes}`;
      }
      let hours = hours24 % 12;
      hours = hours ? hours : 12;
      const ampm = hours24 >= 12 ? 'pm' : 'am';
      return `${hours}:${minutes} ${ampm}`;
    },
    difficultyLabel(value) {
      const map = { easy: 'سهل', medium: 'متوسط', hard: 'صعب' };
      return map[value] || value || '';
    },
    async fetchExam() {
      try {
        const { data } = await axios.get(`/admin/exams/${this.examId}`);
        const exam = data.data;
        this.title = exam.title ?? '';
        this.date = exam.date ? new Date(exam.date) : null;
        this.duration_required = !!exam.duration_required;
        // Backward compatible: NULL/legacy → 'fixed'.
        this.question_order = exam.question_order === 'random' ? 'random' : 'fixed';
        // Backward compatible: NULL/legacy/unknown → 'free'.
        this.navigation_mode = ['sequential', 'sequential_back'].includes(exam.navigation_mode)
          ? exam.navigation_mode
          : 'free';
        this.from_time = this.parseTimeString(exam.from_time);
        this.to_time = this.parseTimeString(exam.to_time);
        this.selectedQuestions = exam.questions ?? [];

        // Resolve the chapter/lesson the exam's lesson belongs to (if any).
        const examLesson = exam.lesson;
        await this.fetchChapters();
        if (examLesson?.parent) {
          this.chapter = this.chapters.find(c => c.id === examLesson.parent.id) || null;
        }
        if (this.chapter) {
          await this.fetchLessons();
          this.lesson = this.lessons.find(l => l.id === examLesson.id) || null;
        }
        await this.fetchQuestions();
        // Keep already-selected questions even if they fall outside the current
        // chapter/lesson filter (e.g. a mixed-lesson exam).
        const known = new Map(this.questions.map(q => [q.id, q]));
        this.selectedQuestions.forEach(q => { if (!known.has(q.id)) known.set(q.id, q); });
        this.selectedQuestions = this.selectedQuestions.map(q => known.get(q.id) || q);
        await this.loadEnhancements(exam);
      } catch (error) {
        Swal.fire({ icon: "error", title: error.response?.data?.message || "تعذر تحميل بيانات الامتحان" });
      } finally {
        this.loading = false;
      }
    },
    sendForm() {
      if (!this.validateEnhancements()) return;
      this.v$.$validate();
      if (this.v$.$error) return;
      if (!this.date || (!this.examSettings.automated && this.question_ids.length === 0)) {
        Swal.fire({ icon: "warning", title: "يرجى ملء الحقول المطلوبة" });
        return;
      }
      this.formSending = true;

      const payload = {
        ...this.enhancementPayload(),
        title: this.title,
        date: this.formatDate(this.date),
        total_degree: this.totalDegree,
        duration_required: this.duration_required ? 1 : 0,
        question_order: this.question_order,
        navigation_mode: this.navigation_mode,
        question_ids: this.question_ids,
      };
      if (this.isCourseScope) {
        payload.course_id = this.courseId;
      } else {
        payload.academic_year_id = this.gradeId;
      }
      if (this.duration_required) {
        payload.from_time = this.formatTimeForBackend(this.from_time, true);
        payload.to_time = this.formatTimeForBackend(this.to_time, true);
        payload.duration = this.duration;
      }
      if (this.lesson) {
        payload.lesson_id = this.lesson.id;
      }

      if (this.examSettings.automated) delete payload.question_ids;
      axios.put(`/exams/${this.examId}`, payload)
          .then(() => {
            Swal.fire({ icon: "success", title: "تم تعديل الامتحان بنجاح" });
            this.$router.push(this.basePath);
          })
          .catch(error => {
            Swal.fire({ icon: "error", title: error.response?.data?.message || "حدث خطأ" });
          })
          .finally(() => {
            this.formSending = false;
          });
    }
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    await this.fetchExam();
  }
};
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-file-export"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل امتحان</h1>
        <p class="form-page-header__subtitle">تعديل بيانات الامتحان وأسئلته</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm" v-if="!loading">
        <!-- Location hierarchy -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">الموقع في المنهج</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الباب</label>
                  <Dropdown v-model="chapter" :options="chapters" showClear :optionLabel="customLabelChapter" placeholder="اختر الباب" class="w-100" @update:model-value="resetLocation('parent')" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الدرس <span class="required">*</span></label>
                  <MultiSelect v-if="examSettings.automated" v-model="lesson_ids" :options="lessons" optionLabel="title" placeholder="اختر الدروس" class="w-100" @update:model-value="fetchQuestions" />
                  <Dropdown v-else v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson" placeholder="اختر الدرس" class="w-100" @update:model-value="fetchQuestions" />
                  <span class="field-error" v-if="v$.lesson.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الدرس
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Exam settings -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">إعدادات الامتحان</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الامتحان <span class="required">*</span></label>
                  <InputText id="title" v-model="title" placeholder="أدخل اسم الامتحان" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الامتحان
                  </span>
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">تاريخ الامتحان <span class="required">*</span></label>
                  <Calendar v-model="date" dateFormat="yy-mm-dd" showIcon class="w-100" />
                  <span class="field-error" v-if="v$.date.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال التاريخ
                  </span>
                </div>
              </div>
            </div>

            <div class="source-toggle mt-4">
              <i class="pi pi-clock" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">تحديد وقت الامتحان</span>
              <InputSwitch v-model="duration_required" />
              <span class="source-toggle__hint">{{ duration_required ? 'سيتم تحديد وقت بداية ونهاية' : 'بدون توقيت محدد' }}</span>
            </div>

            <div class="row g-4 mt-2" v-if="duration_required">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">من الساعة</label>
                  <DatePicker v-model="from_time" hourFormat="12" timeOnly fluid class="w-100" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">إلى الساعة</label>
                  <DatePicker v-model="to_time" hourFormat="12" timeOnly fluid class="w-100" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">المدة المحسوبة</label>
                  <div class="exam-duration-display">
                    <i class="pi pi-clock"></i>
                    <span>{{ duration ? `${duration} دقيقة` : '—' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Question order -->
            <div class="form-field mt-4">
              <label class="field-label">ترتيب الأسئلة</label>
              <div class="type-selector">
                <label v-for="opt in questionOrderOptions" :key="opt.value"
                  :class="['type-selector__item', { active: question_order === opt.value }]">
                  <input type="radio" v-model="question_order" :value="opt.value" class="d-none" />
                  <i :class="`pi ${opt.icon}`"></i>
                  {{ opt.label }}
                </label>
              </div>
            </div>

            <!-- Question navigation -->
            <div class="form-field mt-4">
              <label class="field-label">التنقل بين الأسئلة</label>
              <div class="type-selector">
                <label v-for="opt in navigationModeOptions" :key="opt.value"
                  :class="['type-selector__item', { active: navigation_mode === opt.value }]">
                  <input type="radio" v-model="navigation_mode" :value="opt.value" class="d-none" />
                  <i :class="`pi ${opt.icon}`" style="margin-inline-end:.4rem;"></i>
                  <span style="display:inline-flex;flex-direction:column;text-align:start;line-height:1.3;">
                    <span>{{ opt.label }}</span>
                    <small style="color:var(--text-muted);font-weight:400;">{{ opt.desc }}</small>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <ExamEnhancementSettings v-model="examSettings" :timed="duration_required" />

        <!-- Question selection -->
        <div class="form-card" v-if="!examSettings.automated">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-list-check"></i></div>
            <span class="form-card__title">اختيار الأسئلة <span style="color:var(--color-danger);font-size:0.75rem;">*</span></span>
          </div>
          <div class="form-card__body">
            <div class="exam-search-bar mb-3">
              <i class="pi pi-search exam-search-bar__icon"></i>
              <InputText v-model="searchQuery" placeholder="بحث في الأسئلة بالعنوان أو النوع أو الدرجة..." class="w-100" />
            </div>
            <DataTable
              :value="filteredQuestions"
              selectionMode="multiple"
              :selection="selectedQuestions"
              @update:selection="val => selectedQuestions = val"
              dataKey="id"
              class="w-100"
            >
              <Column selectionMode="multiple" headerStyle="width: 3em"></Column>
              <Column field="title" header="عنوان السؤال"></Column>
              <Column field="type" header="النوع"></Column>
              <Column field="degree" header="الدرجة"></Column>
              <Column header="الصعوبة">
                <template #body="{ data }">{{ difficultyLabel(data.difficulty_level) }}</template>
              </Column>
            </DataTable>
            <span class="field-error mt-2" v-if="v$.question_ids.$error">
              <i class="pi pi-exclamation-circle"></i> برجاء اختيار الأسئلة
            </span>

            <div class="exam-summary-card mt-4" v-if="selectedQuestions.length > 0">
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">عدد الأسئلة</span>
                <span class="exam-summary-card__value">{{ selectedQuestions.length }}</span>
              </div>
              <div class="exam-summary-card__divider"></div>
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">مجموع الدرجات</span>
                <span class="exam-summary-card__value exam-summary-card__value--primary">{{ totalDegree }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
